import App from '@/App.vue'
import { DebugHelper } from '@/helper/debugHelper'
import idb from './idb'

export interface NativeWindow extends Window {
    app?: App
    api?: {
        [id: string]: any
    }
    stringResource: {
        getResource: (id: string) => string
    }
}

declare var window: NativeWindow

export default async () => {
    if (!window.app || !window.api) return;

    // コマンドが実行できるかどうか
    const canExecCommand = (message) => {
        let result = false;

        try {
            if (message != "") {
                const messageObj = JSON.parse(message);
                if (messageObj.isForce) {
                    // 強制実行
                    result = true;
                }
            }
        } catch (e) {
            // JSONではない場合はスルー
        }

        if (!result && window.app && window.app.getModalMode != null && window.app.getModalMode() == 0) {
            // モーダル表示でなければOK
            result = true;
        }

        return result;
    }

    // ドキュメントを元にしてタイトルを得る
    const getWindowTitleByDocument = async () => {
        if (!window.app || !window.api) return;

        // ファイルパス取得
        const currentFilePath = window.app.getDocumentProperty("currentFilePath");
        return currentFilePath
            ? await window.api.getWindowTitleByFilePath(currentFilePath)
            : window.stringResource.getResource("menu_application")
    }

    const saveAsDocument = async () => {
        if (!window.app || !window.api || !canExecCommand("save-as-document")) return;

        // ドキュメントをアプリから取得する
        const documentZip = window.app.getValue("documentAsZip", null);

        // 最終保存パス
        // ファイル名は開いた時のファイルがあれば使う、なければ新規作成
        const useInsertion = window.app.getDocumentProperty("useInsertion");
        const openedFileName = window.app.getDocumentProperty("openedFileName");
        const paperCode = window.app.getDocumentProperty("paperCode");
        const result = await window.api.saveAsDocument(useInsertion, openedFileName, paperCode, documentZip);

        if (result) {
            // ドキュメントに保存パスを保持する
            window.app.setDocumentProperty("currentFilePath", result.currentFilePath)
            // ドキュメントの開いたファイル名を更新する
            window.app.setDocumentProperty("openedFileName", result.openedFileName)

            // 完了メッセージを通知する
            window.app.execCommand("save_document_completed", null);
            return true
        }
        return false
    }

    // ドキュメントを保存
    // 保存済みなら上書き、未保存なら名前をつけて保存
    const saveDocumentMultiWays = async (currentFilePath, documentZip) => {
        if (!window.app || !window.api) return null;

        // ファイルパス取得
        if (currentFilePath && ["aly", "alym"].includes(currentFilePath.split('.').pop())) {
            // ドキュメントの上書き保存
            if (window.api.saveDocument(documentZip, currentFilePath)) {
                // 成功
                return "save";
            } else {
                // 失敗したら別名保存
                return await saveAsDocument()
                    ? "save_as" : null;
            }
        } else {
            // ファイルパスがなければ別名保存
            return await saveAsDocument()
                ? "save_as" : null;
        }
    }

    // タイトルを変更する
    window.api.on("changeWindowTitle", (event, title) => {
        if (window.app) {
            window.app.changeTitle(title);
        }
    })

    window.api.on("set-thumbnail", (_e, thumbnail) => {
        if (!window.app) return;

        window.app.setThumbnail(thumbnail);
    });

    window.api.on("setDocumentProperty", (key, value) => {
        if (!window.app) return;
        window.app.setDocumentProperty(key, value);
    })

    window.api.on("execCommand", (event, command, parameter) => {
        if (!window.app) return;
        DebugHelper.trace(["execCommand", event, command, parameter]);
        window.app.execCommand(command, parameter);
    });

    // コマンドの受信
    window.api.on("do-command", (event, message) => {
        if (!canExecCommand(message)) return;

        let messageObj = JSON.parse(message);
        let command = messageObj.command;
        let parameter =
            messageObj.parameter != null && messageObj.parameter != ""
                ? JSON.parse(messageObj.parameter)
                : null;

        // 実行
        if (window.app) {
            window.app.execCommand(command, parameter);
        }
    });

    // イメージファイルパス読み込み
    window.api.on("import_local_image_path", async (event, message) => {
        if (!window.api || !canExecCommand(message)) return;
        window.api.importLocalImagePath();
    });

    window.api.on("open-file", async (event, filename) => {
        if (!window.app || !window.api || !filename) return;

        if (!window.app.isDocumentModified()) {
            // 変更がなければそのまま開く
            return window.api.openDocument(filename);
        }

        const clicked = await window.api.showConfirmSaveDialog();
        if (clicked === 0) {
            // 保存して開く
            const currentFilePath = window.app.getDocumentProperty("currentFilePath");
            const documentZip = window.app.getValue("documentAsZip", null);

            if (await saveDocumentMultiWays(currentFilePath, documentZip)) {
                window.api.openDocument(filename);
            }
        } else if (clicked === 2) {
            // 保存せずに開く
            window.api.openDocument(filename);
        }
    });

    // ドキュメントを開く
    window.api.on("open-document", async (event, message) => {

        if (!window.app || !window.api || !canExecCommand(message)) return;

        const defaultPath = window.api.getValue("storage_key_document_last_open_directory");

        // ドキュメントの保存フラグがあればダイアログを出す
        if (window.app.isDocumentModified()) {
            // ダイアログ表示
            const result = await window.api.showConfirmSaveDialog();

            if (result == 2) {
                // 保存せず開く
                window.api.openSelectedDocument(defaultPath);
            } else if (result == 0) {
                // 保存する
                const documentZip = window.app.getValue("documentAsZip", null);
                const currentFilePath = window.app.getDocumentProperty("currentFilePath");
                const way = await saveDocumentMultiWays(currentFilePath, documentZip)

                if (!way || way == "save_as") {
                    // 名前をつけて保存の完了・キャンセル時はそのまま何もしない
                    // 開かせてもいいがファイルダイアログが2つ出ると混乱するだろう
                    // noinspection UnnecessaryReturnStatementJS
                    return;
                } else {
                    // 上書き保存完了後
                    // ドキュメントを開く
                    window.api.openSelectedDocument(defaultPath);
                }
            }
        } else {
            // ダイアログを出さずにドキュメントを開く
            window.api.openSelectedDocument(defaultPath);
        }
    });

    window.api.on("open-document-directly", async (event, parameter) => {
        if (!window.app || !parameter.filePath || !parameter.data) return;
        window.app.openFile(parameter.filePath, parameter.data);

        // ウィンドウタイトル更新
        window.app.changeTitle(await getWindowTitleByDocument());
    });

    window.api.on("save_pdf", async () => {
        if (!window.app || !window.api) return

        // 前回の保存先を取得
        const dirname = await window.api.getValue("storage_key_image_last_save_directory");
        window.api.savePDF(dirname);
    })

    window.api.on("savePrintPDF", async (event, filePath) => {
        if (!window.app || !window.api) return

        const dataurl = window.app.getValue("print_pdf");
        const arr = dataurl.split(",");
        const b64 = arr[arr.length - 1];
        if (await window.api.writeFile(filePath, b64, "base64")) {
            event.sender.send("success", filePath)
            window.app.execCommand("save_pdf_completed", null)
        } else {
            event.sender.send("error")
        }
    })

    // ドキュメントの別名保存
    window.api.on("save-as-document", saveAsDocument);

    // ドキュメントの上書き保存
    window.api.on("save-document", async (event, message) => {
        DebugHelper.trace(["save-document", message])
        if (!window.app || !window.api || !canExecCommand(message)) return;

        // ドキュメントをアプリから取得する
        const currentFilePath = window.app.getDocumentProperty("currentFilePath");
        const documentZip = window.app.getValue("documentAsZip", null);
        if (await saveDocumentMultiWays(currentFilePath, documentZip)) {
            // 保存完了時の処理
            window.app.execCommand("save_document_completed", null);
        }
    });

    // イメージの保存
    window.api.on("saveImage", async (event, message) => {
        if (!window.app || !window.api || !canExecCommand(message)) return;

        const image = window.app.getValue("export_image", null);
        const ext = image.indexOf("image/png") != -1 ? "png" : "jpg";
        // ファイル書き込み
        if (window.api) {
            window.api.saveImage(image, ext);
        }
    })

    // パスが変更された
    window.api.on("change_path", async (event, currentPath) => {
        if (!window.app || !window.api) return

        // タイトル変更
        if (
            currentPath == "SelectPage" ||
            (currentPath.length >= 1 &&
                currentPath.substr(currentPath.length - 1, 1) == "#") // TOP
        ) {
            window.app.changeTitle(window.stringResource.getResource("menu_application"));
        } else {
            window.app.changeTitle(await getWindowTitleByDocument());
        }

        // 表示すべきメニューを判定
        let menuType = "";
        if (currentPath == "DesignEditor") {
            menuType = "Edit";
        } else if (currentPath == "LayoutEditor") {
            if (window.app.getDocumentProperty("useInsertion") === true) {
                menuType = "InsertionLayout";
            } else {
                menuType = "Layout";
            }
        } else if (currentPath == "PrintSetting") {
            menuType = "Print";
        } else if (currentPath == "InsertionEditor") {
            menuType = "Insertion";
        }

        // メニュー変更をリクエストする
        window.api.changeMenu(menuType);
    });

    // 差し込みデータを開く
    window.api.on("import-insertion-data", async (event, message) => {
        if (!window.app || !window.api || !canExecCommand(message)) return;

        // 最終オープンパス
        const openPath = window.api.getValue("storage_key_insertion_data_last_open_directory");
        window.api.openInsertionData(openPath);
    });

    // 差し込みデータの書き出し
    window.api.on("export-insertion-data", async (event, message) => {
        if (!window.app || !window.api || !canExecCommand(message)) return;

        const csv = window.app.getValue("insertion_data_as_csv", null);
        window.api.saveInsertionData(csv);
    });

    window.api.on("print_to_pdf", async () => {
        DebugHelper.trace(["print_to_pdf", window.app, window.api]);
        if (!window.app || !window.api) return;

        // 前回の保存先を取得
        const dirname = window.api.getValue("storage_key_image_last_save_directory") || "";
        const ext = "pdf";

        const pageWidth = document.getElementById("pageWidth") as HTMLInputElement;
        const pageHeight = document.getElementById("pageHeight") as HTMLInputElement;
        const width = parseInt(pageWidth.value, 10) * 1000;
        const height = parseInt(pageHeight.value, 10) * 1000;

        window.api.printToPdf(
            dirname,
            ext,
            width,
            height,
        );
    });

    // pageのキャプチャ画像をアプリに送る
    window.api.on("set-page-capture", (_, image) => {
        if (!window.app) return;

        window.app.execCommand("set_page_capture", { image: image });
    });

    // ログインでのメッセージ処理
    window.api.on("process-login-message", (_, query) => {
        if (!window.app) return;

        const queryObj = JSON.parse(query);
        const message = queryObj["value"];
        if (!message) return;

        window.app.execCommand("process_login_message", { message });
    });

    window.api.on("reload-fonts", async () => {
        if (!window.app || !window.api) return;

        // ウィンドウをグレーアウト *念の為30000ms後に戻す
        if ((window.app as any).setVisibleGrayMaskWindow) (window.app as any).setVisibleGrayMaskWindow(true, 30000);

        try {
            await window.app.reloadFontFamily();
            await window.api.openModalMessageBox({
                title: "フォントの再読み込み",
                message: "フォントを再読み込みしました。",
            });
        } catch (e) {
            DebugHelper.trace(e)
            await window.api.openModalMessageBox({
                type: "warning",
                title: "フォントの再読み込み",
                message: "フォントの再読み込みに失敗しました。時間を置いて再試行してください。",
            });
        } finally {
            // ウィンドウのグレーアウトを解除
            if ((window.app as any).setVisibleGrayMaskWindow) {
                (window.app as any).setVisibleGrayMaskWindow(false);
            }
        }
    });

    // 右クリックメニュー
    window.addEventListener("contextmenu", function (e) {
        if (!window.app || !window.api) return;

        const inputMode = window.app.getInputMode();
        if (inputMode == 1 && !window.app.isInTextEdit()) {
            // インプットモード == 1(input, textareaに入力中)
            // ただし、テキストレイヤーエディット中は行わない
            e.preventDefault();
            window.api.openContextMenu();
        }
    });

    // アプリを終了していいか確認する
    window.api.on("confirm_quit", async (_e, _message) => {
        if (!window.app || !window.api) return;

        // まずアプリケーション終了条件をクリアする
        window.api.resetQuit();

        if (!canExecCommand(_message)) {
            // コマンドが実行できない場合はアプリを終了しない
            // 終了待機のリセット
            window.api.resetConfirmQuit();
        }

        if(document.getElementById("questionnaire_view") != null) {
            // アンケート入力中であるなら終了して良いかダイアログを表示する
            const result = await window.api.showAbortUserRegistrationDialog();
            if (result == 0) {
                // 終了する
                window.api.quitFinally();
            }
        } else if (window.app.isDocumentModified()) {
            // ドキュメントの保存フラグがあればダイアログを表示する
            const result = await window.api.showConfirmQuitDialog();
            if (result == 2) {
                // 保存しない
                window.api.quitFinally();
            } else if (result == 0) {
                // 保存する
                const currentFilePath = window.app.getDocumentProperty("currentFilePath");
                const documentZip = window.app.getValue("documentAsZip", null);
                if (await saveDocumentMultiWays(currentFilePath, documentZip)) {
                    // 保存完了時は終了
                    window.api.quitFinally();
                }
            }
        } else {
            // ダイアログを出さずに終了する
            window.api.quitFinally();
        }
        // 終了待機のリセット
        window.api.resetConfirmQuit();
    });

    // イベント初期化を通知
    window.api.windowApiEventsInitialized();

    // DBの初期化
    return idb.init();
}
